(() => {
  const init = (root) => {
    if (!root || root.dataset.ft2Initialized === 'true') return;
    root.dataset.ft2Initialized = 'true';

    const isEn = root.dataset.locale === 'en';
    const locale = isEn ? 'en-CA' : 'fr-CA';
    const currency = root.dataset.currency || 'CAD';
    const formatMoney = (cents) => new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
    const value = (key) => Number(root.dataset[key] || 0);
    const available = (key) => root.dataset[key] === 'true';
    const mainImage = root.querySelector('[data-ft2-main-image]');
    const thumbnails = Array.from(root.querySelectorAll('[data-ft2-thumb]'));
    const indexLabel = root.querySelector('[data-ft2-index]');
    const countLabel = root.querySelector('[data-ft2-count]');
    const quantityLabel = root.querySelector('[data-ft2-quantity]');
    const totalLabel = root.querySelector('[data-ft2-total]');
    const mobileOptionsTotal = root.querySelector('[data-ft2-mobile-options-total]');
    const optionsTotalLabel = root.querySelector('[data-ft2-options-total]');
    const breakdown = root.querySelector('[data-ft2-breakdown]');
    const status = root.querySelector('[data-ft2-status]');
    const addButton = root.querySelector('[data-ft2-add]');
    const installationSelect = root.querySelector('[data-ft2-installation-select]');
    let activeImage = 0;
    let quantity = 1;

    const strings = isEn ? {
      machine: 'Inspire FT2 Pro',
      fid7: 'Inspire FID7 Pro bench',
      scs: 'Inspire SCS bench',
      legs: 'Inspire leg extension/curl + FT2 conversion kit',
      conversion: 'Inspire FT2 conversion kit',
      preacher: 'Inspire preacher curl attachment',
      weights: 'Inspire 50 lb add-on weights (pair)',
      storage: 'Inspire bench accessory storage base',
      delivery: 'Delivery preference',
      installation: 'Installation preference',
      deliver: 'Delivery included in Quebec',
      within: 'Installation within 200 km',
      beyond: 'Installation beyond 200 km — call to confirm',
      stock: 'One or more selected items are currently unavailable. Please update your configuration.',
      error: 'The configuration could not be added to the cart. Please try again.',
      pricing: 'The selected configuration price is temporarily unavailable. Please contact us before ordering.',
      options: 'Selected options',
    } : {
      machine: 'Inspire FT2 Pro',
      fid7: 'Banc Inspire FID7 Pro',
      scs: 'Banc Inspire SCS',
      legs: 'Extension / Curl des jambes Inspire + kit de conversion FT2',
      conversion: 'Kit de conversion Inspire FT2',
      preacher: 'Accessoire pour pupitre à biceps Inspire',
      weights: 'Poids supplémentaires Inspire de 50 lb (paire)',
      storage: 'Support pour accessoires de banc Inspire',
      delivery: 'Préférence de livraison',
      installation: 'Préférence d’installation',
      deliver: 'Livraison incluse au Québec',
      within: 'Installation à moins de 200 km',
      beyond: 'Installation au-delà de 200 km — appeler pour confirmer',
      stock: 'Un ou plusieurs articles sélectionnés ne sont pas disponibles. Veuillez modifier votre configuration.',
      error: 'Impossible d’ajouter la configuration au panier. Veuillez réessayer.',
      pricing: 'Le prix de cette configuration est temporairement indisponible. Veuillez nous contacter avant de commander.',
      options: 'Options sélectionnées',
    };

    const getSelection = () => {
      const bench = root.querySelector('[data-ft2-bench]:checked');
      return {
        bench: bench ? bench.value : null,
        legs: Boolean(root.querySelector('[data-ft2-addon="legs"]:checked')),
        preacher: Boolean(root.querySelector('[data-ft2-addon="preacher"]:checked')),
        weights: Boolean(root.querySelector('[data-ft2-addon="weights"]:checked')),
        storage: Boolean(root.querySelector('[data-ft2-addon="storage"]:checked')),
      };
    };

    const bundleMask = (selection) => (selection.legs ? 1 : 0)
      + (selection.preacher ? 2 : 0)
      + (selection.weights ? 4 : 0);
    const bundleFor = (selection) => {
      const mask = bundleMask(selection);
      return mask ? {
        id: value(`bundle${mask}Id`),
        price: value(`bundle${mask}Price`),
        available: available(`bundle${mask}Available`),
        inventory: value(`bundle${mask}InventoryQuantity`),
      } : {
        id: value('baseId'),
        price: value('basePrice'),
        available: available('baseAvailable'),
        inventory: value('baseInventoryQuantity'),
      };
    };

    const getLines = () => {
      const selection = getSelection();
      const lines = [{ label: strings.machine, cents: value('basePrice'), key: 'base' }];
      if (selection.bench === 'fid7') lines.push({ label: strings.fid7, cents: value('benchFid7Price'), key: 'bench-fid7' });
      if (selection.bench === 'scs') lines.push({ label: strings.scs, cents: value('benchScsPrice'), key: 'bench-scs' });
      if (selection.legs) lines.push({ label: strings.legs, cents: value('comboLegsPrice'), key: 'legs' });
      if (selection.preacher) lines.push({ label: strings.preacher, cents: value('comboPreacherPrice'), key: 'preacher' });
      if (selection.weights) lines.push({ label: strings.weights, cents: value('comboWeightsPrice'), key: 'weights' });
      if (selection.storage) lines.push({ label: strings.storage, cents: value('storagePrice'), key: 'storage' });
      return lines;
    };

    const updateTotals = () => {
      const lines = getLines();
      const selection = getSelection();
      const bundle = bundleFor(selection);
      const benchPrice = selection.bench ? value(selection.bench === 'fid7' ? 'benchFid7Price' : 'benchScsPrice') : 0;
      const storagePrice = selection.storage ? value('storagePrice') : 0;
      const total = (bundle.price + benchPrice + storagePrice) * quantity;
      const matchingPrice = lines.reduce((sum, line) => sum + line.cents, 0) === bundle.price + benchPrice + storagePrice;
      const storageReady = !selection.storage || (value('storageId') && available('storageAvailable') &&
        (!root.dataset.storageInventoryManagement || root.dataset.storageInventoryPolicy !== 'deny' ||
          value('storageInventoryQuantity') >= quantity));
      if (totalLabel) totalLabel.textContent = formatMoney(total);
      if (optionsTotalLabel) optionsTotalLabel.textContent = formatMoney(total);
      if (mobileOptionsTotal) mobileOptionsTotal.hidden = !(
        selection.bench || selection.legs || selection.preacher || selection.weights || selection.storage
      );
      if (breakdown) {
        breakdown.replaceChildren();
        lines.forEach((line) => {
          const item = document.createElement('li');
          item.textContent = `${line.label} — ${formatMoney(line.cents * quantity)}`;
          breakdown.appendChild(item);
        });
      }
      if (quantityLabel) quantityLabel.textContent = String(quantity);
      root.querySelectorAll('[data-ft2-bench-card]').forEach((card) => {
        const input = card.querySelector('[data-ft2-bench]');
        card.classList.toggle('is-selected', Boolean(input && input.checked));
      });
      if (status) status.textContent = matchingPrice && bundle.id ? '' : strings.pricing;
      if (status && !storageReady) status.textContent = strings.stock;
      if (addButton) addButton.disabled = !bundle.id || !bundle.available || !matchingPrice || !storageReady;
    };

    const setImage = (index) => {
      if (!thumbnails.length || !mainImage) return;
      activeImage = (index + thumbnails.length) % thumbnails.length;
      const button = thumbnails[activeImage];
      mainImage.src = button.dataset.src;
      mainImage.alt = button.dataset.alt || '';
      if (indexLabel) indexLabel.textContent = String(activeImage + 1).padStart(2, '0');
      thumbnails.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === activeImage;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-current', String(selected));
      });
    };

    thumbnails.forEach((thumb, index) => thumb.addEventListener('click', () => setImage(index)));
    root.querySelector('[data-ft2-prev]')?.addEventListener('click', () => setImage(activeImage - 1));
    root.querySelector('[data-ft2-next]')?.addEventListener('click', () => setImage(activeImage + 1));
    if (countLabel) countLabel.textContent = String(thumbnails.length).padStart(2, '0');

    root.querySelectorAll('[data-ft2-bench]').forEach((input) => {
      input.addEventListener('change', updateTotals);
    });
    root.querySelector('[data-ft2-clear-bench]')?.addEventListener('click', () => {
      root.querySelectorAll('[data-ft2-bench]').forEach((input) => { input.checked = false; });
      updateTotals();
    });
    root.querySelectorAll('[data-ft2-addon]').forEach((input) => {
      input.addEventListener('change', updateTotals);
    });
    root.querySelector('[data-ft2-minus]')?.addEventListener('click', () => {
      quantity = Math.max(1, quantity - 1);
      updateTotals();
    });
    root.querySelector('[data-ft2-plus]')?.addEventListener('click', () => {
      quantity = Math.min(99, quantity + 1);
      updateTotals();
    });

    const installationNote = root.querySelector('[data-ft2-installation-case]');
    const updateInstallation = () => {
      if (installationNote) installationNote.hidden = installationSelect?.value !== 'case';
    };
    installationSelect?.addEventListener('change', updateInstallation);
    updateInstallation();

    const dialog = root.querySelector('[data-ft2-dialog]');
    const dialogContent = root.querySelector('[data-ft2-dialog-content]');
    let dialogCopy = {};
    try {
      dialogCopy = JSON.parse(root.querySelector('[data-ft2-dialog-copy]')?.textContent || '{}')[isEn ? 'en' : 'fr'] || {};
    } catch (error) {
      console.error('FT2 Pro dialog translations could not be read.', error);
    }
    const priceForGuide = (key) => {
      if (key === 'fid7') return formatMoney(value('benchFid7Price'));
      if (key === 'scs') return formatMoney(value('benchScsPrice'));
      if (key === 'legs') return formatMoney(value('comboLegsPrice'));
      if (key === 'preacher') return formatMoney(value('comboPreacherPrice'));
      if (key === 'weights') return formatMoney(value('comboWeightsPrice'));
      if (key === 'storage') return formatMoney(value('storagePrice'));
      return '';
    };
    const openDialog = (guide) => {
      if (!dialog || !dialogContent) return;
      dialogContent.replaceChildren();
      const heading = document.createElement('h2');
      const paragraph = document.createElement('p');
      const copy = guide === 'included' ? null : dialogCopy.guides?.[guide];
      heading.textContent = guide === 'included' ? dialogCopy.includedTitle : copy?.title || '';
      dialogContent.append(heading);
      if (guide === 'included') {
        paragraph.textContent = dialogCopy.includedIntro || '';
        dialogContent.appendChild(paragraph);
        const list = document.createElement('ul');
        (dialogCopy.included || []).forEach((text) => {
          const item = document.createElement('li');
          item.textContent = text;
          list.appendChild(item);
        });
        dialogContent.appendChild(list);
      } else if (copy) {
        const price = document.createElement('p');
        price.className = 'gd-ft2__dialog-price';
        price.textContent = `${dialogCopy.price}: ${priceForGuide(guide)}`;
        dialogContent.append(price, Object.assign(document.createElement('p'), { textContent: copy.intro }));
        const addSection = (title, items) => {
          const sectionTitle = document.createElement('h3');
          sectionTitle.textContent = title;
          const list = document.createElement('ul');
          items.forEach((text) => {
            const item = document.createElement('li');
            item.textContent = text;
            list.appendChild(item);
          });
          dialogContent.append(sectionTitle, list);
        };
        addSection(dialogCopy.adds, copy.adds || []);
        const chooseTitle = document.createElement('h3');
        chooseTitle.textContent = dialogCopy.choose;
        const chooseText = document.createElement('p');
        chooseText.textContent = copy.choose;
        const compatibilityTitle = document.createElement('h3');
        compatibilityTitle.textContent = dialogCopy.compatibility;
        const compatibilityText = document.createElement('p');
        compatibilityText.textContent = copy.compatibility;
        dialogContent.append(chooseTitle, chooseText, compatibilityTitle, compatibilityText);
        if (copy.url) {
          const link = document.createElement('a');
          link.href = copy.url;
          link.textContent = copy.linkLabel;
          dialogContent.appendChild(link);
        }
        if (copy.alternative) {
          const alternativeTitle = document.createElement('h3');
          alternativeTitle.textContent = dialogCopy.alternative;
          const alternativeText = document.createElement('p');
          alternativeText.textContent = copy.alternative;
          dialogContent.append(alternativeTitle, alternativeText);
        }
      }
      if (!dialog.open) dialog.showModal();
    };
    root.querySelector('[data-ft2-included]')?.addEventListener('click', () => openDialog('included'));
    root.querySelectorAll('[data-ft2-guide]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openDialog(button.dataset.ft2Guide);
      });
    });
    root.querySelector('[data-ft2-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('cancel', (event) => {
      event.stopPropagation();
    });

    const backToPurchase = root.querySelector('[data-ft2-back]');
    const updateBackButton = () => {
      if (!backToPurchase) return;
      const purchase = root.querySelector('#ft-purchase');
      const final = root.querySelector('.gd-ft2__final');
      const visible = Boolean(
        purchase && final &&
        purchase.getBoundingClientRect().bottom < 0 &&
        final.getBoundingClientRect().top > window.innerHeight,
      );
      backToPurchase.hidden = !visible;
    };
    window.addEventListener('scroll', updateBackButton, { passive: true });
    window.addEventListener('resize', updateBackButton);
    backToPurchase?.addEventListener('click', () => {
      root.querySelector('#ft-purchase')?.scrollIntoView({ behavior: 'smooth' });
    });

    const appendInput = (form, name, inputValue) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(inputValue);
      form.appendChild(input);
    };
    const submitConfiguration = () => {
      const selection = getSelection();
      const bundle = bundleFor(selection);
      const benchPrice = selection.bench ? value(selection.bench === 'fid7' ? 'benchFid7Price' : 'benchScsPrice') : 0;
      const storagePrice = selection.storage ? value('storagePrice') : 0;
      const displayedPrice = getLines().reduce((sum, line) => sum + line.cents, 0);
      if (!bundle.id || !bundle.price || displayedPrice !== bundle.price + benchPrice + storagePrice ||
          (selection.storage && (!value('storageId') || !storagePrice))) {
        if (status) status.textContent = strings.pricing;
        return;
      }
      if (!bundle.available || bundle.inventory < quantity || !available('baseAvailable')) {
        if (status) status.textContent = strings.stock;
        return;
      }
      const selectedAvailable = [
        !selection.bench || available(selection.bench === 'fid7' ? 'benchFid7Available' : 'benchScsAvailable'),
        !selection.legs || (available('legsAvailable') && available('conversionAvailable')),
        !selection.preacher || available('preacherAvailable'),
        !selection.weights || available('weightsAvailable'),
        !selection.storage || available('storageAvailable'),
      ].every(Boolean);
      if (!selectedAvailable) {
        if (status) status.textContent = strings.stock;
        return;
      }
      const inventoryLines = [{ key: 'base', quantity }];
      if (selection.bench === 'fid7') inventoryLines.push({ key: 'benchFid7', quantity });
      if (selection.bench === 'scs') inventoryLines.push({ key: 'benchScs', quantity });
      if (selection.legs) {
        inventoryLines.push({ key: 'legs', quantity });
        inventoryLines.push({ key: 'conversion', quantity });
      }
      if (selection.preacher) inventoryLines.push({ key: 'preacher', quantity });
      if (selection.weights) inventoryLines.push({ key: 'weights', quantity: quantity * 2 });
      if (selection.storage) inventoryLines.push({ key: 'storage', quantity });
      const hasInventory = inventoryLines.every((line) => {
        const management = root.dataset[`${line.key}InventoryManagement`];
        const policy = root.dataset[`${line.key}InventoryPolicy`];
        const inventory = Number(root.dataset[`${line.key}InventoryQuantity`] || 0);
        return !management || policy !== 'deny' || inventory >= line.quantity;
      });
      if (!hasInventory) {
        if (status) status.textContent = strings.stock;
        return;
      }
      if (addButton) addButton.disabled = true;
      const form = document.createElement('form');
      form.method = 'post';
      form.action = root.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart/add`;
      form.acceptCharset = 'UTF-8';
      form.style.display = 'none';
      let lineIndex = 0;
      const configuration = [selection.bench, selection.legs ? 'legs' : '', selection.preacher ? 'preacher' : '', selection.weights ? 'weights' : '', selection.storage ? 'storage' : ''].filter(Boolean).join(', ') || (isEn ? 'Base machine only' : 'Station seulement');
      const addLine = (variantId, lineQuantity, properties = {}) => {
        if (!variantId || lineQuantity < 1) return;
        appendInput(form, `items[${lineIndex}][id]`, variantId);
        appendInput(form, `items[${lineIndex}][quantity]`, lineQuantity);
        Object.entries(properties).forEach(([key, property]) => appendInput(form, `items[${lineIndex}][properties][${key}]`, property));
        lineIndex += 1;
      };
      const baseProperties = {
        [strings.delivery]: strings.deliver,
        [strings.installation]: installationSelect?.value === 'case' ? strings.beyond : strings.within,
        [strings.options]: configuration,
      };
      addLine(bundle.id, quantity, baseProperties);
      const linkedProperties = { [strings.options]: configuration };
      if (selection.bench === 'fid7') addLine(value('benchFid7Id'), quantity, linkedProperties);
      if (selection.bench === 'scs') addLine(value('benchScsId'), quantity, linkedProperties);
      if (selection.storage) addLine(value('storageId'), quantity, linkedProperties);
      if (lineIndex === 0) {
        if (status) status.textContent = strings.error;
        if (addButton) addButton.disabled = false;
        return;
      }
      appendInput(form, 'return_to', root.dataset.cartReturn || `${window.Shopify?.routes?.root || '/'}cart`);
      document.body.appendChild(form);
      form.submit();
    };
    addButton?.addEventListener('click', submitConfiguration);

    updateTotals();
    updateBackButton();
  };

  const start = () => document.querySelectorAll('[data-ft2-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-ft2-root]');
    if (section) init(section);
  });
})();